import { z } from "zod";
import { throwIfError } from "@/lib/api/parseResponse";
import { clientFetch } from "@/lib/api/client";
import { proxyPath } from "@/lib/api/paths";
import { DIMENSIONS, type PriorityKey } from "@/app/quiz/_components/quizSchemas";

// Keys are validated against the same vocabulary quiz step 1 renders
// (DIMENSIONS), not accepted as arbitrary strings — an unexpected key from
// Django now fails this parse instead of silently mismatching a `d.key`
// lookup in QuizStepPriorities. fetchPriorityDescriptions's only caller
// (useQuizWizardController) already catches any rejection and falls back to
// {}, so a key-vocabulary drift fails closed (no icons) rather than quietly
// showing an empty tooltip for one row. z.partialRecord (not .record().partial()
// — ZodRecord has no .partial() method) allows any subset of keys, matching
// the endpoint's documented "omit a key rather than send an empty string"
// contract (recommendations/api/views.py: QuestionnairePriorityDescriptionsAPIView).
const priorityKeys = DIMENSIONS.map((d) => d.key) as [PriorityKey, ...PriorityKey[]];
const priorityKeySchema = z.enum(priorityKeys);

const priorityDescriptionsSchema = z.object({
  descriptions: z.partialRecord(priorityKeySchema, z.string()),
});

export type PriorityDescriptions = Partial<Record<PriorityKey, string>>;

export async function fetchPriorityDescriptions(opts?: {
  signal?: AbortSignal;
}): Promise<PriorityDescriptions> {
  const { res, body } = await clientFetch(
    proxyPath("v1/questionnaires/priority-descriptions"),
    { method: "GET", signal: opts?.signal },
  );

  throwIfError(res, body);
  return priorityDescriptionsSchema.parse(body).descriptions;
}
